import * as React from 'react'

import { DocumentUploadDialog, type DocumentUploadMode } from './DocumentUploadDialog'

interface DocumentUploadRequest {
  mode: DocumentUploadMode
  jobId?: string
  contextJobId?: string
  seedFiles?: File[]
}

interface DocumentUploadContextValue {
  openDocumentUpload: (request: DocumentUploadRequest) => void
  documentTransferOpen: boolean
  documentStagingVersion: number
}

const DocumentUploadContext = React.createContext<DocumentUploadContextValue>({
  openDocumentUpload: () => undefined,
  documentTransferOpen: false,
  documentStagingVersion: 0
})

export function useDocumentUpload(): DocumentUploadContextValue {
  return React.useContext(DocumentUploadContext)
}

export function DocumentUploadProvider({ children }: React.PropsWithChildren) {
  const [request, setRequest] = React.useState<DocumentUploadRequest | null>(null)
  const [documentTransferOpen, setDocumentTransferOpen] = React.useState(false)
  const [documentStagingVersion, setDocumentStagingVersion] = React.useState(0)

  const openDocumentUpload = React.useCallback(
    (next: DocumentUploadRequest) => {
      if (!documentTransferOpen) {
        setRequest(next)
        setDocumentStagingVersion((version) => version + 1)
      }
    },
    [documentTransferOpen]
  )
  const contextValue = React.useMemo(
    () => ({ openDocumentUpload, documentTransferOpen, documentStagingVersion }),
    [openDocumentUpload, documentTransferOpen, documentStagingVersion]
  )

  return (
    <DocumentUploadContext.Provider value={contextValue}>
      {children}
      <DocumentUploadDialog
        open={request != null}
        onOpenChange={(open) => {
          if (!open) setRequest(null)
        }}
        mode={request?.mode ?? 'ingest'}
        jobId={request?.jobId}
        contextJobId={request?.contextJobId}
        seedFiles={request?.seedFiles}
        onSeedConsumed={() => {
          setRequest((current) => (current ? { ...current, seedFiles: undefined } : null))
        }}
        transferOpen={documentTransferOpen}
        onTransferOpenChange={setDocumentTransferOpen}
        onComplete={() => {
          setDocumentTransferOpen(false)
          setRequest(null)
        }}
      />
    </DocumentUploadContext.Provider>
  )
}
